import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { LegalClient, LegalRole } from '@powerful/data';

import { useAuthContext } from '../../contexts/auth-context';
import { useLegalReacceptance } from '../../hooks/use-legal-reacceptance';
import { signOut } from '../../lib/auth/auth-client';
import { getSupabaseClient } from '../../lib/supabase';
import { ReacceptancePrompt } from './reacceptance-prompt';

/**
 * Gate jurídico das superfícies privadas (`C7/C16` do PRD · T1.4 Tarefa 5).
 *
 * 🔴 **Falha fechada.** Se a consulta de pendências falhar, o gate mantém o bloqueio em vez de
 * deixar passar: a alternativa — "não consegui verificar, entra à mesma" — produziria uso do
 * produto sem prova de aceite da revisão vigente, que é exatamente o que o P2B e o RGPD exigem
 * conseguir demonstrar depois.
 *
 * O que **nunca** é bloqueado: ler o documento (`/legal/:kind`) e terminar sessão. Prender alguém
 * num diálogo sem saída seria transformar uma atualização de termos em sequestro de conta.
 *
 * A liberação não confia no clique: depois de cada aceite o hook reconsulta o servidor e só
 * desbloqueia quando a resposta traz zero pendências.
 */
export interface LegalReacceptanceGateProps {
  children: ReactNode;
  /** Injetável para teste; em runtime é o client único do app. */
  client?: LegalClient;
  now?: Date;
}

/**
 * Client do app, ou `null` quando as variáveis do Supabase faltam.
 *
 * Sem client não existe sessão — o `AuthProvider` resolve para anónimo no mesmo cenário —, logo
 * não há conteúdo privado a proteger e o gate sai do caminho em vez de bloquear um ecrã vazio.
 */
function lerClient(injected?: LegalClient): LegalClient | null {
  if (injected) return injected;
  try {
    return getSupabaseClient();
  } catch {
    return null;
  }
}

export function LegalReacceptanceGate({ children, client: injectedClient, now }: LegalReacceptanceGateProps) {
  const { i18n } = useTranslation();
  const { user, claims, isLoading } = useAuthContext();
  // 🔴 Memoizado: `useLegalReacceptance` tem o client nas dependências do `reload`. Uma
  // referência nova a cada render transformaria a consulta de pendências num ciclo infinito.
  const client = useMemo(() => lerClient(injectedClient), [injectedClient]);

  const role = (claims.role as LegalRole | null) ?? null;
  const userId = client && !isLoading ? user?.id ?? null : null;

  const { pending, loading, error, acceptingDocumentId, reload, accept } = useLegalReacceptance({
    client: client ?? EMPTY_CLIENT,
    userId,
    locale: i18n.language,
    role,
    ...(now ? { now } : {}),
  });

  const logout = useCallback(() => {
    void signOut();
  }, []);

  // 🔴 `isLoading` também bloqueia: mostrar o conteúdo privado enquanto a sessão resolve e só
  // depois cobri-lo deixaria ver dados por um frame — e um frame basta para uma captura de ecrã.
  const bloqueado =
    isLoading || (userId !== null && role !== null && (pending.length > 0 || Boolean(error) || loading));

  if (bloqueado) {
    return (
      <ReacceptancePrompt
        pending={pending}
        loading={loading || isLoading}
        error={error}
        acceptingDocumentId={acceptingDocumentId}
        onAccept={accept}
        onRetry={() => void reload()}
        onLogout={logout}
      />
    );
  }

  return <>{children}</>;
}

/** Nunca consultado: o hook só chama o client quando existe `userId` e papel. */
const EMPTY_CLIENT = {
  from: () => ({ select: () => ({ data: null, error: null }) }),
} as unknown as LegalClient;
